import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const emptyFilters = {
  groups: [], regions: [], cities: [], segments: [], phone_status: [],
  has_email: null, has_phone: null, is_blacklisted: false,
  tags_any: [], tags_none: [],
  last_activity_after: null, last_activity_before: null,
  activity_score_min: null, activity_score_max: null,
};

export default function SegmentsPage() {
  const [segments, setSegments] = useState([]);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, name, description, color, filters}
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([
        api.get('/segments', { params: { with_counts: true } }),
        api.get('/segments/_facets/values'),
      ]);
      setSegments(s.data.items || []);
      setFacets(f.data || {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing({ name: '', description: '', color: '#7c3aed', filters: { ...emptyFilters } });
    setPreview(null);
  };
  const startEdit = (s) => {
    setEditing({ id: s.id, name: s.name, description: s.description, color: s.color, filters: { ...emptyFilters, ...s.filters } });
    setPreview(null);
  };

  const toggleArr = (key, val) => {
    setEditing(prev => {
      const cur = prev.filters[key] || [];
      const next = cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val];
      return { ...prev, filters: { ...prev.filters, [key]: next } };
    });
  };

  const setFilterValue = (key, val) => {
    setEditing(prev => ({ ...prev, filters: { ...prev.filters, [key]: val } }));
  };

  const runPreview = async () => {
    if (!editing) return;
    setPreviewing(true);
    try {
      const r = await api.post('/segments/preview', editing.filters, { params: { limit: 20 } });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
    finally { setPreviewing(false); }
  };

  const save = async () => {
    if (!editing?.name) { toast.error('Nazwa wymagana'); return; }
    try {
      if (editing.id) {
        await api.put(`/segments/${editing.id}`, { name: editing.name, description: editing.description, color: editing.color, filters: editing.filters });
        toast.success('Zapisano');
      } else {
        await api.post('/segments', { name: editing.name, description: editing.description, color: editing.color, filters: editing.filters });
        toast.success('Utworzono');
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  const del = async (id) => {
    if (!window.confirm('Usunąć segment?')) return;
    try {
      await api.delete(`/segments/${id}`);
      toast.success('Usunięto');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Segmenty kontaktów</h1>
          <p className="text-sm text-muted-foreground">Zapisane filtry do reużycia w kampaniach i agencie</p>
        </div>
        <Button onClick={startNew}>+ Nowy segment</Button>
      </div>

      {loading ? <Skeleton className="h-40" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {segments.map(s => (
            <Card key={s.id} className="cursor-pointer hover:bg-muted/30" onClick={() => startEdit(s)}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ background: s.color }} />
                      <span className="font-semibold">{s.name}</span>
                      <Badge variant="outline" className="text-[10px]">{s.contact_count >= 0 ? s.contact_count : '?'} kontaktów</Badge>
                    </div>
                    {s.description && <p className="text-xs text-muted-foreground mt-1">{s.description}</p>}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.entries(s.filters || {}).filter(([k, v]) => v && (Array.isArray(v) ? v.length : true)).map(([k, v]) => (
                        <Badge key={k} variant="outline" className="text-[10px]">
                          {k}: {Array.isArray(v) ? v.slice(0, 3).join(',') + (v.length > 3 ? '…' : '') : String(v)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); del(s.id); }}>×</Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {segments.length === 0 && <Card><CardContent className="p-6 text-center text-muted-foreground">Brak segmentów. Kliknij "Nowy segment".</CardContent></Card>}
        </div>
      )}

      {/* EDITOR */}
      {editing && (
        <Card className="mt-4 border-primary/30">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <Input value={editing.name} onChange={(e) => setEditing(p => ({ ...p, name: e.target.value }))} placeholder="Nazwa segmentu" />
              <Input value={editing.description} onChange={(e) => setEditing(p => ({ ...p, description: e.target.value }))} placeholder="Opis (opcjonalny)" />
              <Input type="color" value={editing.color} onChange={(e) => setEditing(p => ({ ...p, color: e.target.value }))} />
            </div>

            {/* MULTI-SELECT chips */}
            {['groups', 'regions', 'segments', 'phone_status', 'tags_any', 'tags_none'].map(field => {
              const facetKey = field === 'tags_any' || field === 'tags_none' ? 'tags' : field.replace(/s$/, '').replace('regions', 'region').replace('groups', 'group').replace('segments', 'segment').replace('phone_status', 'phone_status');
              const facetField = field === 'tags_any' || field === 'tags_none' ? 'tags' : (field === 'phone_status' ? 'phone_status' : field.slice(0, -1));
              const opts = facets[facetField] || [];
              const cur = editing.filters[field] || [];
              return (
                <div key={field}>
                  <Label className="capitalize">{field.replace('_', ' ')}</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {opts.slice(0, 30).map(opt => (
                      <Badge
                        key={opt}
                        variant={cur.includes(opt) ? 'default' : 'outline'}
                        className="cursor-pointer text-[10px]"
                        onClick={() => toggleArr(field, opt)}
                      >
                        {opt}
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Ma email?</Label>
                <select value={editing.filters.has_email === null ? '' : String(editing.filters.has_email)} onChange={(e) => setFilterValue('has_email', e.target.value === '' ? null : e.target.value === 'true')} className="w-full mt-1 p-2 rounded border border-border bg-background text-sm">
                  <option value="">dowolne</option>
                  <option value="true">tylko z email</option>
                  <option value="false">tylko bez email</option>
                </select>
              </div>
              <div>
                <Label>Ma telefon?</Label>
                <select value={editing.filters.has_phone === null ? '' : String(editing.filters.has_phone)} onChange={(e) => setFilterValue('has_phone', e.target.value === '' ? null : e.target.value === 'true')} className="w-full mt-1 p-2 rounded border border-border bg-background text-sm">
                  <option value="">dowolne</option>
                  <option value="true">tylko z telefonem</option>
                  <option value="false">tylko bez telefonu</option>
                </select>
              </div>
              <div>
                <Label>Blacklist</Label>
                <select value={editing.filters.is_blacklisted === null ? '' : String(editing.filters.is_blacklisted)} onChange={(e) => setFilterValue('is_blacklisted', e.target.value === '' ? null : e.target.value === 'true')} className="w-full mt-1 p-2 rounded border border-border bg-background text-sm">
                  <option value="false">wyklucz blacklist (domyślnie)</option>
                  <option value="true">tylko blacklisted</option>
                  <option value="">wszystkie</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Activity score min</Label>
                <Input type="number" value={editing.filters.activity_score_min || ''} onChange={(e) => setFilterValue('activity_score_min', e.target.value ? parseInt(e.target.value) : null)} />
              </div>
              <div>
                <Label>Activity score max</Label>
                <Input type="number" value={editing.filters.activity_score_max || ''} onChange={(e) => setFilterValue('activity_score_max', e.target.value ? parseInt(e.target.value) : null)} />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={runPreview} disabled={previewing}>{previewing ? 'Liczę…' : 'Podgląd'}</Button>
              <Button onClick={save}>{editing.id ? 'Zapisz' : 'Utwórz'}</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Anuluj</Button>
            </div>

            {preview && (
              <div className="mt-3 p-3 rounded bg-muted/30">
                <div className="font-semibold mb-2">Podgląd: <span className="text-2xl">{preview.total}</span> kontaktów</div>
                <div className="space-y-1 text-xs">
                  {(preview.sample || []).map(c => (
                    <div key={c.id} className="flex gap-2 p-1 rounded hover:bg-muted/50">
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                      <span className="text-muted-foreground">{c.phone || c.email}</span>
                      <span className="opacity-50">{c.city}</span>
                      <Badge variant="outline" className="text-[9px] ml-auto">{c.segment}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
